"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import {
  Menu,
  X,
  ChevronDown,
  User,
  Settings,
  Check,
  Globe,
  LogOut,
  Building,
  CreditCard,
} from "lucide-react";
import Logo from "./Logo";
import "./Header.css";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMsal } from "@azure/msal-react";
import { googleLogout } from "@react-oauth/google";
import { useAuth } from "../auth/AuthContext";
import {
  getOrgName,
  getUserName,
  normalizeOrganizationSelection,
  setActiveOrganization,
} from "../shared/util";
import { markOrgSwitchNavigation } from "../shared/utils/authSessionState";
import { getUserProfile } from "../../services/sharedService";
import {
  goToWebAppApi,
  updateConsoleUserProfileApi,
  getOrgInfoApi,
} from "../../services/settingsService";
import { logoutApi } from "../../services/authService";
import appConstants from "../shared/config/constants.json";
import { getBillingSubscriptionAccess } from "../shared/access.js";
import { handleApiError as baseHandleApiError } from "../shared/util";
import {
  readPartnerInviteUnread,
  setPartnerInviteUnread,
  subscribePartnerInviteUnread,
  syncPendingPartnerInviteUnread,
} from "../shared/utils/partnerInviteNotificationState";
import {
  readPartnerCollaborationState,
  subscribePartnerCollaborationState,
  writePartnerCollaborationState,
  resetPartnerCollaborationState,
} from "../shared/utils/partnerCollaborationState";
const HEADER_STORAGE_SYNC_EVENT = "maya-header-storage-sync";

const HEADER_STORAGE_KEYS = new Set([
  "organization",
  "selectedOrganization",
  "userName",
]);

const LANGUAGE_STORAGE_KEY = "i18nextLng";

const TALENT_SOLUTIONS_SEEN_KEY = "maya-talent-solutions-seen";

const normalizePartnerCollaboration = (value) =>
  value === true || String(value).toLowerCase() === "true";

const getTalentSolutionsSeenStorageKey = () => {
  if (typeof window === "undefined") {
    return `${TALENT_SOLUTIONS_SEEN_KEY}:default`;
  }

  const organizationKey =
    sessionStorage.getItem("tenantId") ||
    sessionStorage.getItem("mainTenantId") ||
    "default";

  return `${TALENT_SOLUTIONS_SEEN_KEY}:${organizationKey}`;
};

const SUPPORTED_LANGUAGE_CODES = new Set([
  "en",
  "hi",
  "es",
  "zh-CN",
  "ar",
  "fr",
  "pt",
  "de",
  "ja",
  "ru",
  "id",
  "bn",
  "ur",
  "te",
  "ta",
]);

const Header = ({ onLogout }) => {
  const { logout } = useAuth();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [currentPlatform, setCurrentPlatform] = useState("upskilling");
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const [isHamburgerOrgOpen, setIsHamburgerOrgOpen] = useState(false);
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [isOrgLoading, setIsOrgLoading] = useState(false);
  const [hasUnreadPartnerInvite, setHasUnreadPartnerInvite] = useState(
    readPartnerInviteUnread,
  );

  const [partnerCollaboration, setPartnerCollaboration] = useState(() =>
    Boolean(readPartnerCollaborationState()?.enabled),
  );

  useEffect(() => {
    return subscribePartnerCollaborationState((collaborationState) => {
      setPartnerCollaboration(Boolean(collaborationState?.enabled));
    });
  }, []);
  const [showTalentSolutionsNew, setShowTalentSolutionsNew] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const getOrgInfoHandler = async () => {
      try {
        const response = await getOrgInfoApi();

        const apiPartnerCollaboration = response?.data?.partnerCollaboration;

        if (
          apiPartnerCollaboration === undefined ||
          apiPartnerCollaboration === null
        ) {
          console.warn(
            "partnerCollaboration was not returned by getOrgInfoApi.",
          );
          return;
        }

        if (!isMounted) {
          return;
        }

        writePartnerCollaborationState(
          {
            enabled: normalizePartnerCollaboration(apiPartnerCollaboration),
          },
        );
      } catch (error) {
        console.error("Error fetching organization info:", error);
      }
    };

    getOrgInfoHandler();

    return () => {
      isMounted = false;
    };
  }, []);
  useEffect(() => {
    if (!partnerCollaboration) {
      setShowTalentSolutionsNew(false);
      return;
    }

    try {
      const hasBeenOpened =
        localStorage.getItem(getTalentSolutionsSeenStorageKey()) === "true";

      setShowTalentSolutionsNew(!hasBeenOpened);
    } catch (error) {
      console.warn(
        "Unable to read Talent Solutions notification state:",
        error,
      );
      setShowTalentSolutionsNew(false);
    }
  }, [partnerCollaboration]);

  const billingSubscriptionAccess = getBillingSubscriptionAccess();

  useEffect(() => {
    return subscribePartnerInviteUnread(setHasUnreadPartnerInvite);
  }, []);

  useEffect(() => {
    const syncPendingPartnerInviteState = async () => {
      if (!partnerCollaboration) {
        setPartnerInviteUnread(false);
        return;
      }

      try {
        await syncPendingPartnerInviteUnread();
      } catch (error) {
        console.error(
          "Error checking pending partner invitations:",
          error,
        );

        // Important:
        // do NOT clear the existing dot because of a temporary API failure.
      }
    };

    syncPendingPartnerInviteState();
  }, [partnerCollaboration]);

  const navigate = useNavigate();
  const location = useLocation();

  const { instance, accounts } = useMsal();
  const { i18n, t } = useTranslation();

  const dropdownRefs = useRef({});
  const hamburgerRef = useRef(null);
  const mobileNavRef = useRef(null);
  const orgNameRef = useRef(null);
  const orgDialogRef = useRef(null);

  const [headerIdentity, setHeaderIdentity] = useState(() => ({
    userName: getUserName() || t("header.guest"),
    orgName: getOrgName() || "",
  }));

  const { MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL } = appConstants;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideDropdowns = Object.values(dropdownRefs.current).every(
        (ref) => !ref || !ref.contains(event.target),
      );

      const isOutsideHamburger =
        !hamburgerRef.current || !hamburgerRef.current.contains(event.target);

      const isOutsideMobileNav =
        !mobileNavRef.current || !mobileNavRef.current.contains(event.target);

      if (isOutsideDropdowns && isOutsideHamburger && isOutsideMobileNav) {
        setActiveDropdown(null);
        setIsMobileMenuOpen(false);
        setIsHamburgerOrgOpen(false);
        setOpenMobileSection(null);
      }
    };

    if (activeDropdown || isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }

    return undefined;
  }, [activeDropdown, isMobileMenuOpen]);

  const getCurrentPage = () => {
    const path = location.pathname.toLowerCase();

    if (path === "/" || path === "/home") {
      return "home";
    }

    return path.startsWith("/") ? path.substring(1) : path;
  };
  const currentPage = getCurrentPage();

  const readOrgTenantId = (org) => normalizeOrganizationSelection(org).tenantId;

  const readOrgCodeFromItem = (org) =>
    normalizeOrganizationSelection(org).orgCode;

  const readOrgNameFromItem = (org) =>
    normalizeOrganizationSelection(org).organization;

  const readOrgKey = (org, fallback) =>
    readOrgCodeFromItem(org) || readOrgTenantId(org) || fallback;

  const syncHeaderIdentity = useCallback(() => {
    const next = {
      userName: getUserName() || t("header.guest"),
      orgName: getOrgName() || "",
    };

    setHeaderIdentity((previous) =>
      previous.userName === next.userName && previous.orgName === next.orgName
        ? previous
        : next,
    );
  }, [t]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (window.__mayaHeaderStoragePatched) {
      return;
    }

    window.__mayaHeaderStoragePatched = true;

    const storagePrototype = Storage.prototype;

    const originalSetItem = storagePrototype.setItem;
    const originalRemoveItem = storagePrototype.removeItem;
    const originalClear = storagePrototype.clear;

    const emitStorageSync = (key = null) => {
      window.dispatchEvent(
        new CustomEvent(HEADER_STORAGE_SYNC_EVENT, {
          detail: { key },
        }),
      );
    };

    storagePrototype.setItem = function setItem(key, value) {
      originalSetItem.call(this, key, value);
      emitStorageSync(key);
    };

    storagePrototype.removeItem = function removeItem(key) {
      originalRemoveItem.call(this, key);
      emitStorageSync(key);
    };

    storagePrototype.clear = function clear() {
      originalClear.call(this);
      emitStorageSync();
    };
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (!event?.key || HEADER_STORAGE_KEYS.has(event.key)) {
        syncHeaderIdentity();
      }
    };

    const handlePatchedStorage = (event) => {
      const key = event?.detail?.key;

      if (!key || HEADER_STORAGE_KEYS.has(key)) {
        syncHeaderIdentity();
      }
    };

    const handleWindowFocus = () => {
      syncHeaderIdentity();
    };

    syncHeaderIdentity();

    window.addEventListener("storage", handleStorage);

    window.addEventListener(HEADER_STORAGE_SYNC_EVENT, handlePatchedStorage);

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      window.removeEventListener("storage", handleStorage);

      window.removeEventListener(
        HEADER_STORAGE_SYNC_EVENT,
        handlePatchedStorage,
      );

      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [syncHeaderIdentity]);

  const normalizeLanguageCode = (languageCode) => {
    if (!languageCode || typeof languageCode !== "string") {
      return "en";
    }

    if (SUPPORTED_LANGUAGE_CODES.has(languageCode)) {
      return languageCode;
    }

    if (languageCode.toLowerCase() === "zh") {
      return "zh-CN";
    }

    const shortLanguageCode = languageCode.split("-")[0].toLowerCase();

    if (SUPPORTED_LANGUAGE_CODES.has(shortLanguageCode)) {
      return shortLanguageCode;
    }

    return "en";
  };

  const getUserProfileApiHandler = async () => {
    setIsOrgLoading(true);

    try {
      const response = await getUserProfile(
        sessionStorage.getItem("mainTenantId"),
      );

      //const apiLanguage = response?.data?.language || "en";
      //const languageToApply = normalizeLanguageCode(apiLanguage);
      const languageToApply = "en";

      // Remove the old language key to prevent conflicting values.
      sessionStorage.removeItem("language");

      // Save the backend language using the same key used by i18next.
      sessionStorage.setItem(LANGUAGE_STORAGE_KEY, languageToApply);

      const currentLanguage = i18n.resolvedLanguage || i18n.language || "en";

      const normalizedCurrentLanguage = normalizeLanguageCode(currentLanguage);

      if (normalizedCurrentLanguage !== languageToApply) {
        await i18n.changeLanguage(languageToApply);
      }

      setOrganizations(response?.data?.organizations || []);
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setIsOrgLoading(false);
    }
  };

  useEffect(() => {
    getUserProfileApiHandler();

    // The profile should be fetched once when the header mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (selectedLanguage) => {
    const payload = {
      email: sessionStorage.getItem("email"),
      name: sessionStorage.getItem("userName"),
      language: selectedLanguage,
      defaultOrg: sessionStorage.getItem("tenantId"),
    };

    console.log("Updating language payload:", payload);

    const response = await updateConsoleUserProfileApi(payload);

    console.log("Language update API response:", response);

    const statusCode =
      response?.code ??
      response?.status ??
      response?.data?.code ??
      response?.data?.status;

    if (statusCode !== 200) {
      throw new Error("Language update failed");
    }

    return response;
  };

  const handleLanguageChange = async (languageCode) => {
    if (!SUPPORTED_LANGUAGE_CODES.has(languageCode)) {
      return;
    }

    const currentLanguage = i18n.resolvedLanguage || i18n.language || "en";

    const previousLanguage = normalizeLanguageCode(currentLanguage);

    if (previousLanguage === languageCode) {
      setIsLanguageModalOpen(false);
      return;
    }

    try {
      // Update session storage immediately.
      sessionStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);

      // Update the UI immediately.
      await i18n.changeLanguage(languageCode);

      setIsLanguageModalOpen(false);

      // Save the selected language in the backend.
      await handleSave(languageCode);
    } catch (error) {
      console.error("Error updating language:", error);

      // Restore the previous language when the API fails.
      sessionStorage.setItem(LANGUAGE_STORAGE_KEY, previousLanguage);

      await i18n.changeLanguage(previousLanguage);

      baseHandleApiError(error, {
        showModal: false,
      });
    }
  };

  const handleOrgChange = (org) => {
    console.log("Selected organization:", org);

    const selection = normalizeOrganizationSelection(org);

    if (!selection.tenantId) {
      console.warn("Selected organization is missing tenant id:", org);

      return;
    }

    setActiveOrganization(selection);
    resetPartnerCollaborationState();
    setPartnerInviteUnread(false);
    markOrgSwitchNavigation();
    syncHeaderIdentity();

    navigate("/home");
    window.location.reload();
  };

  useEffect(() => {
    if (!isOrgDialogOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      const clickedOutsideDialog =
        orgDialogRef.current && !orgDialogRef.current.contains(event.target);

      const clickedOutsideTrigger =
        orgNameRef.current && !orgNameRef.current.contains(event.target);

      if (clickedOutsideDialog && clickedOutsideTrigger) {
        setIsOrgDialogOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOrgDialogOpen]);

  const markTalentSolutionsSeen = () => {
    try {
      localStorage.setItem(getTalentSolutionsSeenStorageKey(), "true");
    } catch (error) {
      console.warn(
        "Unable to save Talent Solutions notification state:",
        error,
      );
    }

    setShowTalentSolutionsNew(false);
  };

  const handleNavigation = (page) => {
    if (String(page).includes("talent-development/hiring")) {
      markTalentSolutionsSeen();
    }

    navigate(page);
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
    setOpenMobileSection(null);
  };

  const languages = [
    {
      name: "english",
      value: "en",
      flag: "🇺🇸",
    },
    {
      name: "hindi",
      value: "hi",
      flag: "🇮🇳",
    },
    {
      name: "spanish",
      value: "es",
      flag: "🇪🇸",
    },
    {
      name: "chinese",
      value: "zh-CN",
      flag: "🇨🇳",
    },
    {
      name: "arabic",
      value: "ar",
      flag: "🇸🇦",
    },
    {
      name: "french",
      value: "fr",
      flag: "🇫🇷",
    },
    {
      name: "portuguese",
      value: "pt",
      flag: "🇧🇷",
    },
    {
      name: "german",
      value: "de",
      flag: "🇩🇪",
    },
    {
      name: "japanese",
      value: "ja",
      flag: "🇯🇵",
    },
    {
      name: "russian",
      value: "ru",
      flag: "🇷🇺",
    },
    {
      name: "indonesian",
      value: "id",
      flag: "🇮🇩",
    },
    {
      name: "bengali",
      value: "bn",
      flag: "🇧🇩",
    },
    {
      name: "urdu",
      value: "ur",
      flag: "🇵🇰",
    },
    {
      name: "telugu",
      value: "te",
      flag: "🇮🇳",
    },
    {
      name: "tamil",
      value: "ta",
      flag: "🇮🇳",
    },
  ];

  const rawActiveLanguage = i18n.resolvedLanguage || i18n.language || "en";

  const activeLanguage = normalizeLanguageCode(rawActiveLanguage);

  const getCurrentLanguageName = () => {
    const currentLanguage = languages.find(
      (language) => language.value === activeLanguage,
    );

    return currentLanguage
      ? t(`header.languages.${currentLanguage.name}`)
      : t("header.languages.english");
  };

  const navigationItems = [
    {
      id: "talent-development",
      titleKey: "nav.upskilling",
      icon: ChevronDown,
      items: [
        {
          id: "talent-development/upskill",
          titleKey: "nav.upskillInsights",
          description: "nav.upskillInsightsDescription",
        },
        {
          id: "talent-development/leadership",
          titleKey: "nav.leadershipPotential",
          description: "nav.leadershipPotentialDescription",
        },
        {
          id: "talent-development/mobility",
          titleKey: "nav.internalMobility",
          description: "nav.internalMobilityDescription",
        },
      ],
    },

    {
      id: "search-explore",
      titleKey: "nav.people",
      icon: ChevronDown,
      items: [
        {
          id: "employees",
          titleKey: "nav.profiles",
          description: "nav.employeesDescription",
        },
        {
          id: "teams",
          titleKey: "nav.teams",
          description: "nav.teamsDescription",
        },
        {
          id: "talent-development/nominations",
          titleKey: "nav.leadershipNominations",
          description: "nav.leadershipNominationsDescription",
        },
        {
          id: "talent-development/recommendations",
          titleKey: "nav.mobilityRecommendations",
          description: "nav.mobilityRecommendationsDescription",
        },
      ],
    },
    {
      id: "tia",
      titleKey: "nav.talentIntelligence",
      icon: ChevronDown,
      items: [
        {
          id: "/tia",
          titleKey: "nav.tia",
          description: "nav.tiaDescription",
        },
      ],
    },

    ...(partnerCollaboration
      ? [
          {
            id: "talent-solutions",
            titleKey: "Talent Solutions",
            icon: ChevronDown,
            items: [
              {
                id: "talent-development/hiring",
                titleKey: "Hiring Solutions",
                description: "Identify best-fit profiles",
              },
            ],
          },
        ]
      : []),
  ];

  const toggleDropdown = (dropdownId) => {
    setActiveDropdown((previousDropdown) =>
      previousDropdown === dropdownId ? null : dropdownId,
    );
  };

  const isPageInDropdown = (navItem) => {
    if (!currentPage) {
      return false;
    }

    const directMatch = navItem.items.some((item) => item.id === currentPage);

    if (directMatch) {
      return true;
    }

    return navItem.items.some((item) => {
      if (currentPage === item.id) {
        return true;
      }

      if (currentPage.startsWith(`${item.id}/`)) {
        return true;
      }

      if (
        item.id === "employees" &&
        (currentPage.includes("employees") || currentPage.includes("people"))
      ) {
        return true;
      }

      if (item.id === "teams" && currentPage.includes("teams")) {
        return true;
      }

      if (item.id === "job-roles" && currentPage.includes("job-roles")) {
        return true;
      }

      if (currentPage === "home" && navItem.id === "talent-development") {
        return false;
      }

      return false;
    });
  };

  const isItemActive = (itemId) => {
    if (!currentPage) {
      return false;
    }

    if (currentPage === itemId) {
      return true;
    }

    if (currentPage.startsWith(`${itemId}/`)) {
      return true;
    }

    if (
      itemId === "employees" &&
      (currentPage.includes("employees") || currentPage.includes("people"))
    ) {
      return true;
    }

    if (itemId === "teams" && currentPage.includes("teams")) {
      return true;
    }

    if (itemId === "job-roles" && currentPage.includes("job-roles")) {
      return true;
    }

    return false;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((previous) => !previous);
  };

  const ensureOrigin = (base) => {
    if (/^https?:\/\//i.test(base)) {
      return base.replace(/\/+$/, "");
    }

    const scheme = /^(localhost|127\.0\.0\.1)(:|$)/i.test(base)
      ? "http://"
      : "https://";

    return `${scheme}${base}`.replace(/\/+$/, "");
  };

  const ENV_BASES = {
    local:
      (import.meta.env?.VITE_LOCAL_BASE_URL &&
        ensureOrigin(import.meta.env.VITE_LOCAL_BASE_URL)) ||
      "http://localhost:4300",

    dev:
      (import.meta.env?.VITE_DEV_BASE_URL &&
        ensureOrigin(import.meta.env.VITE_DEV_BASE_URL)) ||
      "https://talent-dev.mayamaya.ai",

    uat:
      (import.meta.env?.VITE_UAT_BASE_URL &&
        ensureOrigin(import.meta.env.VITE_UAT_BASE_URL)) ||
      "https://talent-test.mayamaya.ai",

    prod:
      (import.meta.env?.VITE_PROD_BASE_URL &&
        ensureOrigin(import.meta.env.VITE_PROD_BASE_URL)) ||
      "https://talent.mayamaya.ai",
  };

  const ENV_LOGOUT_PATHS = {
    local: import.meta.env?.VITE_LOCAL_LOGOUT_PATH || "/signin",

    dev: import.meta.env?.VITE_DEV_LOGOUT_PATH || "/signin",

    uat: import.meta.env?.VITE_UAT_LOGOUT_PATH || "/signin",

    prod: import.meta.env?.VITE_PROD_LOGOUT_PATH || "/signin",
  };

  const resolveEnv = () => {
    const explicit = (
      import.meta.env?.VITE_ENV ||
      process.env?.REACT_APP_ENV ||
      ""
    ).toLowerCase();

    if (["local", "dev", "uat", "prod"].includes(explicit)) {
      return explicit;
    }

    const host = window.location.hostname.toLowerCase();

    if (host === "localhost" || host === "127.0.0.1") {
      return "local";
    }

    if (host.includes("dev")) {
      return "dev";
    }

    if (
      host.includes("test") ||
      host.includes("uat") ||
      host.includes("staging") ||
      host.includes("qa")
    ) {
      return "uat";
    }

    return "prod";
  };

  const getLogoutUrl = () => {
    const environment = resolveEnv();

    const base = ENV_BASES[environment] || ensureOrigin(window.location.origin);

    const path = ENV_LOGOUT_PATHS[environment] || "/signin";

    return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const clearAppStorage = () => {
    try {
      for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
        const key = sessionStorage.key(index);

        if (key && !key.startsWith("msal.")) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn("Failed to clear sessionStorage:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch (error) {
      console.error("Backend logout failed:", error);
    }

    const redirectUrl = getLogoutUrl();

    setActiveDropdown(null);
    setIsMobileMenuOpen(false);

    try {
      logout();
    } catch (error) {
      console.warn("logout threw:", error);
    }

    try {
      onLogout?.();
    } catch (error) {
      console.warn("onLogout threw:", error);
    }

    const isMicrosoftLogin = Array.isArray(accounts) && accounts.length > 0;

    if (isMicrosoftLogin) {
      try {
        const account = instance.getActiveAccount?.() || accounts[0];

        await instance.logoutRedirect({
          account,
          onRedirectNavigate: () => false,
        });
      } catch (error) {
        console.warn("MSAL local logout failed:", error);

        try {
          instance.setActiveAccount?.(null);
        } catch (setAccountError) {
          console.warn("Failed to clear active MSAL account:", setAccountError);
        }
      }
    } else {
      try {
        googleLogout();
      } catch (error) {
        console.warn("Google logout failed:", error);
      }
    }

    clearAppStorage();
    window.location.replace(redirectUrl);
  };

  const handlePlatformSwitch = (platform) => {
    setCurrentPlatform(platform);

    if (platform === "mayamaya") {
      WebAppNavigationHandler();
    }
  };

  const WebAppNavigationHandler = async () => {
    try {
      const payload = {
        email: sessionStorage.getItem("email"),
      };

      const response = await goToWebAppApi(payload);

      if (response?.code === 200 && response?.data) {
        window.location.assign(response.data);
        setCurrentPlatform("upskilling");
      }
    } catch (error) {
      console.error("Error navigating to assessment platform:", error);
    }
  };

  return (
    <>
      <header className="main-header">
        <div className="container">
          <div className="header-content">
            <div
              className="logo"
              onClick={() => handleNavigation("/home")}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleNavigation("/home");
                }
              }}
            >
              <Logo width={80} height={40} className="logo-svg" />
            </div>

            <nav className="desktop-nav">
              {navigationItems.map((navItem) => (
                <div
                  key={navItem.id}
                  className="nav-dropdown"
                  style={{
                    fontFamily: '"Titillium Web", sans-serif',
                  }}
                  ref={(element) => {
                    dropdownRefs.current[navItem.id] = element;
                  }}
                >
                  <button
                    type="button"
                    className={`nav-button ${
                      activeDropdown === navItem.id || isPageInDropdown(navItem)
                        ? "active"
                        : ""
                    } ${
                      navItem.id === "talent-solutions" &&
                      showTalentSolutionsNew
                        ? "new-feature"
                        : ""
                    }`}
                    onClick={() => toggleDropdown(navItem.id)}
                  >
                    <span className="header-nav-label">
                      {t(navItem.titleKey)}

                      {navItem.id === "talent-solutions" &&
                        showTalentSolutionsNew && (
                          <span className="header-new-feature-badge">New</span>
                        )}
                    </span>

                    <navItem.icon size={16} />
                  </button>

                  {activeDropdown === navItem.id && (
                    <div className="dropdown-menu">
                      {navItem.items.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          className={`dropdown-item ${
                            isItemActive(item.id) ? "active" : ""
                          }`}
                          onClick={() => handleNavigation(item.id)}
                        >
                          <div className="dropdown-item-content">
                            <span className="dropdown-item-title">
                              {t(item.titleKey)}
                            </span>

                            <span className="dropdown-item-description">
                              {t(item.description)}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <div className="header-right">
              <div className="platform-switch-wrapper">
                <button
                  type="button"
                  className={`platform-switch-button ${currentPlatform}`}
                  onClick={() =>
                    handlePlatformSwitch(
                      currentPlatform === "upskilling"
                        ? "mayamaya"
                        : "upskilling",
                    )
                  }
                  title={t("header.platform.switchTitle")}
                  aria-label={t("header.platform.switchAriaLabel")}
                >
                  <div className="platform-switch-track">
                    <div className="platform-switch-option upskilling-option">
                      <span
                        className="platform-name"
                        style={{
                          paddingRight: "8px",
                        }}
                      >
                        {t("header.platform.businessConsole")}
                      </span>
                    </div>

                    <div className="platform-switch-option mayamaya-option">
                      <span className="platform-name">
                        {t("header.platform.assessment")}
                      </span>
                    </div>
                  </div>

                  <div className="platform-switch-indicator" />
                </button>
              </div>

              <div className="user-info">
                <p>
                  {t("header.welcome")}, {headerIdentity.userName}
                </p>

                <div className="org-switcher" ref={orgNameRef}>
                  <button
                    type="button"
                    className="org-trigger"
                    onClick={() => {
                      const next = !isOrgDialogOpen;

                      setIsOrgDialogOpen(next);

                      if (next) {
                        getUserProfileApiHandler();
                      }
                    }}
                  >
                    <span>{headerIdentity.orgName}</span>

                    <ChevronDown size={18} />
                  </button>

                  {isOrgDialogOpen && (
                    <div ref={orgDialogRef} className="org-dialog-popover">
                      {isOrgLoading ? (
                        <div>
                          {[...Array(3)].map((_, index) => (
                            <div
                              key={index}
                              className="org-list-item-skeleton"
                            />
                          ))}
                        </div>
                      ) : (
                        organizations.map((org, index) => (
                          <button
                            type="button"
                            className="org-list-item"
                            key={readOrgKey(org, index)}
                            style={{
                              marginBottom: "10px",
                              cursor: "pointer",
                            }}
                            onClick={() => handleOrgChange(org)}
                          >
                            <Building size={16} />

                            <span>{readOrgNameFromItem(org)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="hamburger-menu" ref={hamburgerRef}>
                <button
                  type="button"
                  className={`hamburger-button ${
                    activeDropdown === "hamburger" ? "active" : ""
                  }`}
                  onClick={() => toggleDropdown("hamburger")}
                >
                  <Menu size={20} />
                  {hasUnreadPartnerInvite && (
                    <span
                      className="header-partner-notification-dot"
                      aria-label="Pending partner invitation"
                    />
                  )}
                </button>

                {activeDropdown === "hamburger" && (
                  <div className="dropdown-menu hamburger-dropdown">
                    <div className="hamburger-mobile-only">
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setIsHamburgerOrgOpen((previous) => {
                            const next = !previous;

                            if (next && organizations.length === 0) {
                              getUserProfileApiHandler();
                            }

                            return next;
                          });
                        }}
                      >
                        <Building size={16} />

                        <span>{t("header.organization")}</span>

                        <span className="hamburger-org-current">
                          {headerIdentity.orgName}
                        </span>

                        <ChevronDown
                          size={16}
                          className={isHamburgerOrgOpen ? "rotated" : ""}
                        />
                      </button>

                      {isHamburgerOrgOpen && (
                        <div className="hamburger-submenu">
                          {isOrgLoading ? (
                            <div
                              style={{
                                padding: "8px 12px",
                              }}
                            >
                              {t("header.loading")}
                            </div>
                          ) : (
                            organizations.map((org, index) => (
                              <button
                                type="button"
                                key={readOrgKey(org, index)}
                                className="hamburger-submenu-item"
                                onClick={() => handleOrgChange(org)}
                              >
                                <Building size={16} />

                                <span>{readOrgNameFromItem(org)}</span>
                              </button>
                            ))
                          )}
                        </div>
                      )}

                      <div className="dropdown-divider" />

                      {navigationItems.map((navItem) => (
                        <div key={navItem.id} className="hamburger-nav-section">
                          <button
                            type="button"
                            className="hamburger-section-title"
                            onClick={() =>
                              setOpenMobileSection((previous) =>
                                previous === navItem.id ? null : navItem.id,
                              )
                            }
                          >
                            <span className="header-nav-label">
                              {t(navItem.titleKey)}

                              {navItem.id === "talent-solutions" &&
                                showTalentSolutionsNew && (
                                  <span className="header-new-feature-badge">
                                    New
                                  </span>
                                )}
                            </span>

                            <ChevronDown
                              size={18}
                              className={
                                openMobileSection === navItem.id
                                  ? "rotated"
                                  : ""
                              }
                            />
                          </button>

                          {openMobileSection === navItem.id && (
                            <div className="hamburger-section-items">
                              {navItem.items.map((item) => (
                                <button
                                  type="button"
                                  key={item.id}
                                  className={`dropdown-item ${
                                    isItemActive(item.id) ? "active" : ""
                                  }`}
                                  onClick={() => handleNavigation(item.id)}
                                >
                                  <span>{t(item.titleKey)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}

                      <div className="dropdown-divider" />
                    </div>

                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleNavigation("settings/profile")}
                    >
                      <User size={16} />

                      <span>{t("header.myProfile")}</span>
                    </button>

                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleNavigation("settings")}
                    >
                      <Settings size={16} />

                      <span>{t("header.settings")}</span>

                      {hasUnreadPartnerInvite && (
                        <span
                          className="header-settings-notification-dot"
                          aria-label="Pending partner invitation in settings"
                        />
                      )}
                    </button>

                    {billingSubscriptionAccess.canViewPage && (
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => handleNavigation("manage-billings")}
                      >
                        <CreditCard size={16} />

                        <span>{t("header.manageBilling")}</span>
                      </button>
                    )}

                    {/* <button
                      type="button"
                      onClick={() => {
                        setIsLanguageModalOpen(true);

                        setActiveDropdown(null);
                      }}
                      className="dropdown-item"
                    >
                      <Globe size={16} />

                      <span>{t("header.language")}</span>

                      <span className="language-current">
                        {getCurrentLanguageName()}
                      </span>
                    </button> */}

                    <div className="dropdown-divider" />

                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => {
                        window.location.href = MAYAMAYA_PROD_WHITELABEL_SUPPORT_URL;
                      }}
                    >
                      <span>{t("header.helpSupport")}</span>
                    </button>

                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleNavigation("legal/terms")}
                    >
                      <span>{t("header.termsConditions")}</span>
                    </button>

                    <button
                      type="button"
                      className="dropdown-item"
                      onClick={() => handleNavigation("legal/privacy")}
                    >
                      <span>{t("header.privacyPolicy")}</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="dropdown-item"
                    >
                      <LogOut size={16} />

                      <span>{t("header.logout")}</span>
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                className="mobile-menu-toggle"
                onClick={toggleMobileMenu}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="mobile-nav" ref={mobileNavRef}>
              {navigationItems.map((navItem) => (
                <div key={navItem.id} className="mobile-nav-section">
                  <button
                    type="button"
                    className={`mobile-nav-title-btn ${
                      openMobileSection === navItem.id ? "open" : ""
                    }`}
                    onClick={() =>
                      setOpenMobileSection((previous) =>
                        previous === navItem.id ? null : navItem.id,
                      )
                    }
                  >
                    <span className="header-nav-label">
                      {t(navItem.titleKey)}

                      {navItem.id === "talent-solutions" &&
                        showTalentSolutionsNew && (
                          <span className="header-new-feature-badge">New</span>
                        )}
                    </span>

                    <ChevronDown size={18} className="mobile-nav-title-icon" />
                  </button>

                  {openMobileSection === navItem.id && (
                    <div className="mobile-nav-items">
                      {navItem.items.map((item) => (
                        <button
                          type="button"
                          key={item.id}
                          className={`mobile-nav-item ${
                            isItemActive(item.id) ? "active" : ""
                          }`}
                          onClick={() => handleNavigation(item.id)}
                        >
                          <span className="mobile-nav-item-title">
                            {t(item.titleKey)}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      {isLanguageModalOpen && (
        <div
          className="language-modal-overlay"
          onClick={() => setIsLanguageModalOpen(false)}
        >
          <div
            className="language-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="language-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="language-modal-header">
              <h3 id="language-modal-title">{t("header.selectLanguage")}</h3>

              <button
                type="button"
                className="language-modal-close"
                onClick={() => setIsLanguageModalOpen(false)}
                aria-label={t("header.closeLanguageModal", {
                  defaultValue: "Close language selection",
                })}
              >
                <X size={20} />
              </button>
            </div>

            <div className="language-list">
              {languages.map((language) => {
                const isSelected = activeLanguage === language.value;

                return (
                  <button
                    type="button"
                    key={language.value}
                    className={`language-item ${isSelected ? "selected" : ""}`}
                    onClick={() => handleLanguageChange(language.value)}
                  >
                    <span className="language-flag">{language.flag}</span>

                    <span className="language-name">
                      {t(`header.languages.${language.name}`)}
                    </span>

                    {isSelected && (
                      <Check size={16} className="language-check" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
